import { Construction, Lock, RotateCw, SearchX, ServerCrash, Unplug } from 'lucide-react';
import { isApiError } from '@/api/errors';
import { Button } from '@/components/ui/Button';
import { CopyButton } from '@/components/ui/CopyId';
import { EmptyState } from '@/components/ui/EmptyState';
import { cn } from '@/lib/cn';

/** Shown when the endpoint behind a screen is not deployed yet (404 ROUTE_NOT_FOUND). */
export function EndpointPending({
  endpoint,
  className,
}: {
  endpoint?: string | undefined;
  className?: string;
}) {
  return (
    <EmptyState
      icon={Construction}
      className={className}
      title="Not available yet"
      description={
        <>
          This part of the Biller API hasn’t shipped in this environment.
          {endpoint && (
            <>
              {' '}
              The console will light up as soon as{' '}
              <code className="rounded-xs bg-muted px-1 py-px mono text-fg">{endpoint}</code> responds.
            </>
          )}
        </>
      }
    />
  );
}

export function NoAccess({ ability, className }: { ability?: string; className?: string }) {
  return (
    <EmptyState
      icon={Lock}
      className={className}
      title="No access"
      description={
        ability ? (
          <>
            Your role does not include{' '}
            <code className="rounded-xs bg-muted px-1 py-px mono text-fg">{ability}</code>. Ask an owner to
            change it.
          </>
        ) : (
          'Your role does not allow this. Ask an owner to change it.'
        )
      }
    />
  );
}

/** One error renderer for every query: pending endpoint, no access, not found, offline, generic. */
export function QueryError({
  error,
  onRetry,
  className,
  resourceLabel = 'resource',
}: {
  error: unknown;
  onRetry?: () => void;
  className?: string;
  resourceLabel?: string;
}) {
  if (isApiError(error)) {
    if (error.isRouteMissing)
      return <EndpointPending endpoint={`${error.method} /v1${error.path}`} className={className} />;
    if (error.isForbidden) return <NoAccess className={className} />;
    if (error.isNotFound) {
      return (
        <EmptyState
          icon={SearchX}
          className={className}
          title={`This ${resourceLabel} doesn’t exist`}
          description="It may belong to the other environment (test/live) or another tenant."
        />
      );
    }
    if (error.isNetwork) {
      return (
        <EmptyState
          icon={Unplug}
          className={className}
          title="Can’t reach the API"
          description={error.detail}
          action={
            onRetry && (
              <Button onClick={onRetry} icon={<RotateCw />}>
                Try again
              </Button>
            )
          }
        />
      );
    }
  }

  const detail = isApiError(error) ? error.detail : error instanceof Error ? error.message : String(error);
  const requestId = isApiError(error) ? error.requestId : null;
  return (
    <EmptyState
      icon={ServerCrash}
      className={cn(className)}
      title="Something went wrong"
      description={
        <>
          {detail}
          {requestId && (
            <span className="mt-2 flex items-center justify-center gap-1 text-sm">
              <span className="text-fg-subtle">Request</span>
              <span className="mono text-fg-muted">{requestId}</span>
              <CopyButton value={requestId} label="Copy request id" />
            </span>
          )}
        </>
      }
      action={
        onRetry && (
          <Button onClick={onRetry} icon={<RotateCw />}>
            Try again
          </Button>
        )
      }
    />
  );
}
